// ─────────────────────────────────────────────────────────────────────
// NAVIGATION — the app-level view model.
//
// `Padel24AppMain` is a hand-rolled `view`-string state machine. This module
// is the first piece of the navigation layer (see docs/history/DECOMPOSITION_PLAN.md):
// it gives the machine a typed `View` union and the one genuinely-duplicated
// predicate (`isLiveTournamentView`). The per-site `type→view` maps are
// deliberately NOT unified here — they differ by context (different keys,
// fallbacks, and klubbmesterskap/seriespill handling), so collapsing them
// would change behaviour.
// ─────────────────────────────────────────────────────────────────────

/** Every value the app-level `view` state can take (the nav state-machine). */
export type View =
  // hubs / landing
  | 'home'
  | 'createTournament'
  | 'miniTournaments'
  | 'otherFormats'
  | 'clubSettings'
  // setup / wizard views
  | 'wizard'
  | 'americanoSetup'
  | 'teamAmericanoSetup'
  | 'mexicanoSetup'
  | 'mixicanoSetup'
  | 'teamMexicanoSetup'
  | 'mixAmericanoSetup'
  | 'clubAmericanoSetup'
  | 'clubTeamAmericanoSetup'
  | 'clubMexicanoSetup'
  | 'klubbmesterskapSetup'
  | 'seriespillSetup'
  // live tournament views
  | 'tournament'
  | 'americano'
  | 'mexicano'
  | 'mixicano'
  | 'teamMexicano'
  | 'mixAmericano'
  | 'clubAmericano'
  | 'clubTeamAmericano'
  | 'clubMexicano'
  | 'klubbmesterskap'
  // series / standalone
  | 'seriespill'
  | 'singleMatches'
  // feature pages
  | 'bookings'
  | 'activities'
  | 'banepad'
  | 'mobile'
  | 'singleMatchesBanepad'
  | 'players'
  | 'archive'
  | 'reporting'
  | 'messages'
  // account (#383) — the signed-in club user's own account
  | 'account'
  // the organization's own «Tilganger» page (slice 3, 2026-10-18) — reached
  // from club settings, by an access manager
  | 'access'
  // the organization's own «Samarbeid» page (slice 4a, 2026-10-20) — reached
  // from club settings, by somebody who manages co-operation (Eier by default)
  | 'links'
  // the organization's own «Logg» (#935, 2026-11-01) — reached from club
  // settings, by somebody who reads the organization's audit trail
  | 'auditLog'
  // a club books a centre's courts through a co-operation (slice 4c, 2026-10-20)
  // — reached from the home page by whoever arranges play
  | 'linkBooking'
  // a centre arranges activities in a club's name through a co-operation
  // (slice 4d, 2026-10-21) — reached from the centre's home page
  | 'linkActivities'
  // info / meta
  | 'help'
  | 'bugs'
  | 'changelog'
  | 'engine';

/**
 * The "live tournament" views — the running Americano-family + Padel24 screens.
 * Mirrors the monolith's `inTournamentView` predicate exactly (note: it does
 * NOT include `klubbmesterskap` or `seriespill`, which have their own flows).
 * Used to decide auto-park-before-navigate and the header "Aktiv" shortcut.
 */
export const LIVE_TOURNAMENT_VIEWS = [
  'tournament',
  'americano',
  'mexicano',
  'mixicano',
  'teamMexicano',
  'mixAmericano',
  'clubAmericano',
  'clubTeamAmericano',
  'clubMexicano',
] as const satisfies readonly View[];

const LIVE_TOURNAMENT_VIEW_SET: ReadonlySet<string> = new Set(LIVE_TOURNAMENT_VIEWS);

/** True when `view` is one of the running Americano-family / Padel24 screens. */
export function isLiveTournamentView(view: string): boolean {
  return LIVE_TOURNAMENT_VIEW_SET.has(view);
}
